/**
 * ArtefactsPanel Component
 *
 * Main panel component with tabbed interface for Citations and Generated Content.
 * Integrates with ChatStore artefacts state and provides smooth HeroUI experience.
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import { Tabs, Tab, Button, addToast, Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";
import { useBrand, useUser, useAuth } from "@webuild-ai/react-auth";

import { useArtefactStore } from "@/stores/ArtefactStore";
import { batchExport, BatchExportFormat } from "@/utils/artefactExport";
import { Artefact, ArtefactType } from "@/types/artefacts";
import { useTheme } from "@/hooks/use-theme";
import { logger } from "@/telemetry";

import { ArtefactExportDialog } from "./ArtefactExportDialog";
import { CitationsList } from "./CitationsList";
import { ArtefactsList } from "./ArtefactsList";

export const ArtefactsPanel: React.FC = () => {
  const { getFilteredArtefactsForCurrentChat, getCitationsForCurrentChat, toggleArtefacts } =
    useArtefactStore();
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { userProfile } = useUser();
  const { user } = useAuth();
  const { theme } = useTheme();
  const authorName = userProfile?.name || user?.name || brand.name;
  const tabsRef = useRef<HTMLDivElement>(null);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const artefacts = getFilteredArtefactsForCurrentChat();
  const hasArtefacts = artefacts.length > 0;

  const citations = getCitationsForCurrentChat();
  const hasCitations = citations.length > 0;

  // Hooks must be called before any conditional returns (Rules of Hooks)
  const [selectedTab, setSelectedTab] = useState<string>(hasCitations ? "citations" : "artefacts");

  // Update selected tab when content availability changes
  useEffect(() => {
    if (!hasCitations && hasArtefacts && selectedTab === "citations") {
      setSelectedTab("artefacts");
    } else if (!hasArtefacts && hasCitations && selectedTab === "artefacts") {
      setSelectedTab("citations");
    }
  }, [hasArtefacts, hasCitations, selectedTab]);

  // Handle batch export
  const handleBatchExport = useCallback(
    async (
      orderedArtefacts: Artefact[],
      format: BatchExportFormat,
      title?: string,
      useSeparateWorksheets?: boolean,
      fontFamily?: string,
      codeFontFamily?: string,
    ) => {
      // Count HTML artefacts for better progress messaging
      const htmlCount = orderedArtefacts.filter((a) => a.type === ArtefactType.HTML).length;

      // Show specific progress for HTML capture if present
      if (htmlCount > 0) {
        addToast({
          title: t("Capturing {{count}} charts...", { count: htmlCount }),
          description: t("Please wait while we prepare your images"),
          color: "default",
        });
      } else {
        const formatKey =
          format === "powerpoint"
            ? t("Exporting to PowerPoint...")
            : format === "word"
              ? t("Exporting to Word...")
              : format === "pdf"
                ? t("Exporting to PDF...")
                : t("Exporting to Excel...");

        addToast({
          title: formatKey,
          description: t("Please wait while we prepare your export"),
          color: "default",
        });
      }

      try {
        await batchExport(orderedArtefacts, {
          format,
          title: title || t("Generated Content Export"),
          author: authorName,
          useSeparateWorksheets,
          brand,
          brandName: brand.name,
          theme,
          fontFamily,
          codeFontFamily,
        });

        addToast({
          title: t("Export successful!"),
          description: t("Your content has been exported"),
          color: "success",
        });
      } catch (error) {
        logger.error("Batch export failed", error);
        addToast({
          title: t("Export failed"),
          description: t("Unable to export content. Please try again."),
          color: "danger",
        });
      }
    },
    [t, brand, theme, authorName],
  );

  // Don't render the panel at all if there's nothing to show
  if (!hasArtefacts && !hasCitations) {
    return null;
  }

  // Focus first interactive element when panel opens
  useEffect(() => {
    // Small delay to account for animation
    const focusTimeout = setTimeout(() => {
      const firstButton = tabsRef.current?.querySelector('button[role="tab"]') as HTMLElement;

      firstButton?.focus();
    }, 100);

    return () => clearTimeout(focusTimeout);
  }, []); // Run once on mount

  // Add keyboard handlers for ] (toggle panel) and Cmd/Ctrl+E (export)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input field
      const activeElement = document.activeElement;
      const isTyping =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA" ||
        (activeElement as HTMLElement)?.isContentEditable;

      if (isTyping) return;

      // ] key - toggle panel closed
      if (e.key === "]") {
        toggleArtefacts();
        // Return focus to trigger button (use data-testid which is language-independent)
        const triggerButton = document.querySelector(
          '[data-testid="artefacts-toggle"]',
        ) as HTMLElement;

        triggerButton?.focus();
      }

      // Cmd/Ctrl+E - open export dialog (only if artefacts exist and dialog is not already open)
      // Check both lowercase and uppercase 'e'/'E'
      if ((e.metaKey || e.ctrlKey) && (e.key === "e" || e.key === "E")) {
        logger.info("🔍 Ctrl/Cmd+E detected, checking conditions", {
          hasArtefacts: String(hasArtefacts),
          isExportDialogOpen: String(isExportDialogOpen),
          shouldOpen: String(hasArtefacts && !isExportDialogOpen),
        });

        if (hasArtefacts && !isExportDialogOpen) {
          e.preventDefault();
          e.stopPropagation();
          logger.info("✅ Opening export dialog via keyboard shortcut");
          setIsExportDialogOpen(true);
        }
      }
    };

    // Use capture phase to intercept events before they reach child elements
    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [toggleArtefacts, hasArtefacts, isExportDialogOpen]);

  return (
    <div
      aria-label={t("Artefacts and citations panel")}
      className="w-full h-full bg-background flex flex-col"
      role="complementary"
    >
      {/* Tab header */}
      <div
        ref={tabsRef}
        className="flex items-center justify-between border-b border-divider bg-background px-3"
      >
        <Tabs
          aria-label="Content tabs"
          classNames={{
            base: "flex-1",
            tabList: "gap-6 w-full relative rounded-none p-0 bg-transparent",
            cursor: "w-full bg-brand-primary",
            tab: "max-w-fit px-4 h-12 data-[selected=true]:text-white",
            tabContent:
              "group-data-[selected=true]:text-white! text-brand-primary dark:text-white! font-medium",
          }}
          selectedKey={selectedTab}
          onSelectionChange={(key) => setSelectedTab(key as string)}
        >
          {hasCitations && (
            <Tab
              key="citations"
              title={
                <div className="flex items-center gap-2">
                  <Icon icon="solar:document-text-outline" width={16} />
                  <span>{t("Citations")}</span>
                  {citations.length > 0 && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        selectedTab === "citations"
                          ? "bg-white/20 text-white"
                          : "bg-brand-primary/10 text-brand-primary dark:bg-white/10 dark:text-white/70"
                      }`}
                    >
                      {citations.length}
                    </span>
                  )}
                </div>
              }
            />
          )}
          {hasArtefacts && (
            <Tab
              key="artefacts"
              title={
                <div className="flex items-center gap-2">
                  <Icon
                    className={
                      selectedTab === "artefacts"
                        ? "text-white!"
                        : "text-brand-primary dark:text-white!"
                    }
                    icon="solar:code-bold"
                    width={16}
                  />
                  <span
                    className={
                      selectedTab === "artefacts"
                        ? "text-white!"
                        : "text-brand-primary dark:text-white!"
                    }
                  >
                    {t("Generated Content")}
                  </span>
                  {artefacts.length > 0 && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        selectedTab === "artefacts"
                          ? "bg-white/20 text-white!"
                          : "bg-brand-primary/10 text-brand-primary dark:bg-white/10 dark:text-white!"
                      }`}
                    >
                      {artefacts.length}
                    </span>
                  )}
                </div>
              }
            />
          )}
        </Tabs>

        {/* Export Button - Show when artefacts exist */}
        {hasArtefacts && artefacts.length >= 1 && (
          <Tooltip content={t("Download")}>
            <Button
              isIconOnly
              aria-label={
                artefacts.length === 1 ? t("Export content") : t("Export all content (Ctrl/Cmd+E)")
              }
              className="ml-2"
              size="sm"
              variant="light"
              onPress={() => setIsExportDialogOpen(true)}
            >
              <Icon icon="solar:export-bold" width={20} />
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Tab panels */}
      <div className="flex-1 overflow-hidden">
        {selectedTab === "citations" && hasCitations && <CitationsList />}
        {selectedTab === "artefacts" && hasArtefacts && <ArtefactsList />}
      </div>

      {/* Export Dialog */}
      <ArtefactExportDialog
        artefacts={artefacts}
        brand={brand}
        isOpen={isExportDialogOpen}
        theme={theme}
        onClose={() => setIsExportDialogOpen(false)}
        onExport={handleBatchExport}
      />
    </div>
  );
};
